import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMetadataStudioThemeAssets } from '../../data/metadataStudioAssets.js';
import HomeFlutedGlass from './HomeFlutedGlass.jsx';
import HomeGrainGradient from './HomeGrainGradient.jsx';
import '../../styles/MetadataStudioStory.css';

const PHOTO_ASSET_ROOT = '/home-redesign/photos/optimized';
const STORY_ASSET_ROOT = '/home-redesign/story/optimized';
const ANALYSIS_ASSET_SRC = `${STORY_ASSET_ROOT}/alpine-hiker-640.webp`;
const ANALYSIS_ASSET_SRC_SET = `${STORY_ASSET_ROOT}/alpine-hiker-640.webp 640w, ${STORY_ASSET_ROOT}/alpine-hiker-960.webp 960w`;

const scrollSceneToViewportAnchor = (node, behavior = 'auto') => {
  if (!node || typeof window === 'undefined') return;
  const rect = node.getBoundingClientRect();
  const viewportAnchor = window.innerHeight * 0.46;
  const targetTop = Math.max(
    0,
    window.scrollY + rect.top + (rect.height / 2) - viewportAnchor
  );
  window.scrollTo({ top: targetTop, behavior });
};

const DEFAULT_COPY = {
  eyebrow: 'Inside Metadata Studio',
  title: 'One upload. Eight visible decisions. One export-ready batch.',
  description: 'Follow the same asset from intake to delivery. Every scene focuses on a real control in Metadata Studio, so the workflow is shown instead of merely listed.',
  progressLabel: 'Metadata Studio story progress',
  sceneLabel: 'Studio scene',
  stepLabel: 'Step',
  resultLabel: 'What changes',
  openStudio: 'Open Metadata Studio',
  studioFrameLabel: 'Interactive representation of the Metadata Studio workflow',
  scenes: [
    {
      id: 'upload',
      kicker: 'Batch intake',
      title: 'Upload once. Keep the whole batch in one queue.',
      description: 'Photos, transparent assets, vectors and video enter the same workspace. The queue keeps processing state visible without a file-by-file handoff.',
      result: 'Every asset stays attached to one review session.'
    },
    {
      id: 'analysis',
      kicker: 'Visual analysis',
      title: 'See what the model reads before metadata is written.',
      description: 'The selected asset stays in view while subject, setting, visual style and commercial concepts are surfaced. Deep Analysis remains a visible, optional control.',
      result: 'The metadata starts from the image, not from a random keyword list.'
    },
    {
      id: 'title',
      kicker: 'Title controls',
      title: 'Generate a useful title, then keep control of its length.',
      description: 'A complete title appears in the editor beside the selected asset. Length and capitalization controls remain available before the batch moves forward.',
      result: 'Generated does not mean locked.'
    },
    {
      id: 'description',
      kicker: 'Description + instructions',
      title: 'Shape the description with your own production rules.',
      description: 'Description length, custom instructions, prefix and postfix settings sit beside an editable result, keeping the AI output inside the contributor’s workflow.',
      result: 'The same instruction can stay consistent across the batch.'
    },
    {
      id: 'keywords',
      kicker: 'Keyword intelligence',
      title: 'Order by relevance. Remove repeats before export.',
      description: 'The strongest terms lead the set, duplicate keywords are removed and prohibited-word rules stay visible. Keyword count remains adjustable for the destination.',
      result: 'The first keywords carry intent instead of noise.'
    },
    {
      id: 'batch',
      kicker: 'Batch editing',
      title: 'Apply one edit to every selected asset—and undo it.',
      description: 'Add or remove a keyword, find and replace text, or normalize a field across the selection. Undo keeps bulk changes reversible during review.',
      result: 'Repeated cleanup becomes one controlled action.'
    },
    {
      id: 'platform',
      kicker: 'Platform presets',
      title: 'Map one reviewed set to the destination you choose.',
      description: 'Switch between real marketplace presets and see the output columns change with the platform. Category selection remains part of the same review flow.',
      result: 'No second metadata rewrite for the next marketplace.'
    },
    {
      id: 'export',
      kicker: 'Export ready',
      title: 'Leave with files that are ready for the upload step.',
      description: 'CSV, XMP, embedded metadata and ZIP delivery finish the workflow. The selected platform format stays explicit before the export is created.',
      result: 'The raw asset and reviewed metadata leave together.'
    }
  ],
  ui: {
    appName: 'Metadata Studio',
    workflowLabel: 'Studio workflow',
    journeyBadge: 'End-to-end asset journey',
    controlsBadge: 'Live interactive controls',
    exportBadge: 'Multi-platform export',
    active: 'Active',
    phase: 'Phase',
    verified: 'Live workflow verified',
    workspace: 'Workspace',
    queue: 'Upload queue',
    files: 'files',
    processing: 'Processing',
    complete: 'Complete',
    review: 'Review',
    selected: 'selected',
    visualAnalysis: 'Visual Analysis',
    deepAnalysis: 'Deep Analysis',
    subject: 'Subject',
    setting: 'Setting',
    artStyle: 'Visual style',
    concept: 'Commercial concept',
    title: 'Title',
    titleLength: 'Title length',
    medium: '12–26 words',
    description: 'Description',
    descriptionLength: 'Description length',
    customInstructions: 'Custom instructions',
    prefix: 'Prefix',
    postfix: 'Postfix',
    keywords: 'Keywords',
    keywordCount: 'Keyword count',
    relevance: 'Relevance order',
    duplicateRemoved: 'Duplicate removed',
    prohibitedWords: 'Prohibited words',
    bulkEdit: 'Bulk edit',
    addKeyword: 'Add keyword',
    removeKeyword: 'Remove keyword',
    findReplace: 'Find and replace',
    undo: 'Undo',
    platformPreset: 'Platform preset',
    category: 'Category',
    outputColumns: 'Output columns',
    exportFormats: 'Export formats',
    embedded: 'Embedded metadata',
    ready: 'Ready to upload',
    generatedTitle: 'Solo hiker overlooking a calm alpine lake at sunrise',
    generatedDescription: 'A solo hiker in a rust jacket overlooks a calm alpine lake as the first morning light reaches the mountains.',
    instruction: 'Keep the description factual and commercially useful.',
    categoryValue: 'Travel',
    queueStatus: '5 / 5 ready',
    batchStatus: '4 files selected',
    undoStatus: 'Bulk keyword edit can be undone',
    undoRestored: 'Previous metadata restored',
    applyEdit: 'Apply edit',
    exportStatus: 'Metadata package ready',
    assetAlt: 'Hiker beside a calm alpine lake used throughout the Metadata Studio story',
    analysisSubject: 'hiker',
    analysisSetting: 'alpine lake',
    analysisStyle: 'travel photo',
    analysisConcept: 'adventure',
    adobeColumns: 'Filename · Title · Keywords · Category',
    shutterstockColumns: 'Filename · Title · Description · Keywords · Category'
  }
};

const STORY_FILES = [
  {
    name: 'alpine-hiker.jpg',
    type: 'JPG',
    src: `${STORY_ASSET_ROOT}/alpine-hiker-640.webp`,
    srcSet: `${STORY_ASSET_ROOT}/alpine-hiker-640.webp 640w, ${STORY_ASSET_ROOT}/alpine-hiker-960.webp 960w`
  },
  {
    name: 'reusable-bottle.jpg',
    type: 'JPG',
    src: `${STORY_ASSET_ROOT}/reusable-bottle-320.webp`,
    srcSet: `${STORY_ASSET_ROOT}/reusable-bottle-320.webp 320w, ${STORY_ASSET_ROOT}/reusable-bottle-640.webp 640w`
  },
  {
    name: 'transparent-object.png',
    type: 'PNG'
  },
  {
    name: 'sustainable-city.eps',
    type: 'EPS',
    src: `${STORY_ASSET_ROOT}/sustainable-city-vector-320.webp`,
    srcSet: `${STORY_ASSET_ROOT}/sustainable-city-vector-320.webp 320w, ${STORY_ASSET_ROOT}/sustainable-city-vector-640.webp 640w`
  },
  {
    name: 'city-clip.mp4',
    type: 'VIDEO',
    src: `${PHOTO_ASSET_ROOT}/city-sunset-160.webp`,
    srcSet: `${PHOTO_ASSET_ROOT}/city-sunset-96.webp 96w, ${PHOTO_ASSET_ROOT}/city-sunset-160.webp 160w`
  }
];

const KEYWORDS = [
  'hiker',
  'mountain lake',
  'adventure',
  'travel',
  'alpine',
  'landscape',
  'outdoors',
  'reflection'
];

const mergeStoryCopy = (content, copy) => {
  const contentObject = Array.isArray(content) ? { scenes: content } : (content || {});
  const copyObject = copy || {};
  const sceneOverrides = contentObject.scenes || copyObject.scenes || [];
  const scenes = DEFAULT_COPY.scenes.map((scene, index) => ({
    ...scene,
    ...(copyObject.scenes?.[index] || {}),
    ...(contentObject.scenes?.[index] || {})
  }));

  return {
    ...DEFAULT_COPY,
    ...copyObject,
    ...contentObject,
    title: contentObject.title || contentObject.flowTitle || copyObject.title || DEFAULT_COPY.title,
    description: contentObject.description || contentObject.flowDesc || copyObject.description || DEFAULT_COPY.description,
    scenes: sceneOverrides.length ? scenes : DEFAULT_COPY.scenes,
    ui: {
      ...DEFAULT_COPY.ui,
      ...(copyObject.ui || {}),
      ...(contentObject.ui || {})
    }
  };
};

const handleActionClick = (event, callback) => {
  if (
    !callback
    || event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
  ) return;

  event.preventDefault();
  callback(event);
};

const QueueFiles = ({ compact = false }) => (
  <div className={`mss-queue-files ${compact ? 'mss-queue-files--compact' : ''}`}>
    {STORY_FILES.map((file, index) => (
      <div className="mss-queue-file" key={file.name} style={{ '--mss-file-index': index }}>
        <span className="mss-file-check" aria-hidden="true">✓</span>
        {file.src ? (
          <img
            src={file.src}
            srcSet={file.srcSet}
            sizes="56px"
            width="160"
            height="160"
            loading="lazy"
            decoding="async"
            alt=""
          />
        ) : <span className="mss-generic-file-preview" aria-hidden="true">{file.type}</span>}
        <span className="mss-file-copy">
          <strong>{file.name}</strong>
          <small>{file.type}</small>
        </span>
      </div>
    ))}
  </div>
);

const AnalysisFlutedReveal = ({ viewport }) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const viewportQuery = window.matchMedia(
      viewport === 'mobile' ? '(max-width: 1099px)' : '(min-width: 1100px)'
    );
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!viewportQuery.matches || reducedMotionQuery.matches) return undefined;

    setShouldRender(true);
    const unmountTimer = window.setTimeout(() => setShouldRender(false), 1700);
    return () => window.clearTimeout(unmountTimer);
  }, [viewport]);

  if (!shouldRender) return null;

  return (
    <HomeFlutedGlass
      className="mss-fluted-glass-reveal"
      data-analysis-effect="fluted-glass"
      image={ANALYSIS_ASSET_SRC}
      maxPixelCount={360000}
    />
  );
};

const AssetPreview = ({ ui, analysis = false, flutedReveal = '' }) => (
  <figure className={`mss-asset-preview ${analysis ? 'is-analysis' : ''}`}>
    <img
      src={ANALYSIS_ASSET_SRC}
      srcSet={ANALYSIS_ASSET_SRC_SET}
      sizes="(max-width: 1099px) calc(100vw - 64px), 420px"
      width="960"
      height="640"
      loading="lazy"
      decoding="async"
      alt={ui.assetAlt}
    />
    {analysis ? (
      <>
        {flutedReveal ? <AnalysisFlutedReveal viewport={flutedReveal} /> : null}
        <div className="mss-analysis-overlay" aria-label={ui.visualAnalysis}>
          <span className="mss-analysis-marker mss-analysis-marker--subject"><i />{ui.subject}: {ui.analysisSubject} <em className="mss-confidence">99%</em></span>
          <span className="mss-analysis-marker mss-analysis-marker--setting"><i />{ui.setting}: {ui.analysisSetting} <em className="mss-confidence">98%</em></span>
          <span className="mss-analysis-marker mss-analysis-marker--style"><i />{ui.artStyle}: {ui.analysisStyle} <em className="mss-confidence">97%</em></span>
          <span className="mss-analysis-marker mss-analysis-marker--concept"><i />{ui.concept}: {ui.analysisConcept} <em className="mss-confidence">95%</em></span>
        </div>
      </>
    ) : null}
  </figure>
);

const FieldPreview = ({ label, value, meta, multiline = false, active = false }) => (
  <div className={`mss-field-preview ${multiline ? 'mss-field-preview--multiline' : ''} ${active ? 'is-focused' : ''}`}>
    <span className="mss-field-label">{label}</span>
    <p>{value}</p>
    {meta ? <small>{meta}</small> : null}
  </div>
);

const KeywordPreview = ({ ui, compact = false }) => {
  const keywords = Array.isArray(ui.sampleKeywords) && ui.sampleKeywords.length ? ui.sampleKeywords : KEYWORDS;
  return (
  <div className={`mss-keyword-preview ${compact ? 'mss-keyword-preview--compact' : ''}`}>
    <div className="mss-keyword-head">
      <strong>{ui.keywords}</strong>
      <span>{ui.keywordCount}: {keywords.length}</span>
    </div>
    <ol>
      {keywords.map((keyword, index) => (
        <li key={keyword} style={{ '--mss-keyword-index': index }}>
          <span>{index + 1}</span><strong>{keyword}</strong><small>{Math.max(96 - (index * 5), 61)}%</small>
        </li>
      ))}
      <li className="is-duplicate" style={{ '--mss-keyword-index': keywords.length }}>
        <span>×</span><strong>{keywords[1] || 'mountain lake'}</strong><small>{ui.duplicateRemoved}</small>
      </li>
    </ol>
    <div className="mss-keyword-rules">
      <span>✓ {ui.relevance}</span>
      <span>✓ {ui.prohibitedWords}</span>
    </div>
  </div>
  );
};

const BatchPreview = ({ ui, edited, onEditChange, compact = false }) => (
  <div className={`mss-batch-preview ${edited ? 'is-edited' : 'is-restored'} ${compact ? 'mss-batch-preview--compact' : ''}`}>
    <div className="mss-batch-selection">
      {STORY_FILES.slice(0, 4).map((file) => (
        <span key={file.name}>
          {file.src
            ? <img src={file.src} width="160" height="160" loading="lazy" decoding="async" alt="" />
            : <span className="mss-generic-file-preview" aria-hidden="true">{file.type}</span>}
          <i aria-hidden="true">✓</i>
        </span>
      ))}
    </div>
    <div className="mss-batch-toolbar">
      <div><strong>{ui.bulkEdit}</strong><small>{ui.batchStatus}</small></div>
      <span>{ui.addKeyword}: <b>{edited ? 'travel' : '—'}</b></span>
      <span>{ui.removeKeyword}: <b>{edited ? 'tourism' : '—'}</b></span>
      <span>{ui.findReplace}</span>
      <button
        type="button"
        aria-label={edited ? ui.undo : ui.applyEdit}
        onClick={() => onEditChange(!edited)}
      ><span aria-hidden="true">↶</span>{edited ? ui.undo : ui.applyEdit}</button>
    </div>
    <p className="mss-undo-toast"><span aria-hidden="true">✓</span>{edited ? ui.undoStatus : ui.undoRestored}</p>
  </div>
);

const PlatformPreview = ({ ui, platform, onPlatformChange, compact = false }) => {
  const isAdobe = platform === 'adobe';
  return (
    <div className={`mss-platform-preview ${compact ? 'mss-platform-preview--compact' : ''}`}>
      <span className="mss-preview-label">{ui.platformPreset}</span>
      <div className="mss-platform-switch" aria-label={ui.platformPreset}>
        <button
          type="button"
          className={isAdobe ? 'is-active' : ''}
          aria-pressed={isAdobe}
          onClick={() => onPlatformChange('adobe')}
        >Adobe Stock</button>
        <button
          type="button"
          className={!isAdobe ? 'is-active' : ''}
          aria-pressed={!isAdobe}
          onClick={() => onPlatformChange('shutterstock')}
        >Shutterstock</button>
      </div>
      <div className="mss-platform-output">
        <span><small>{ui.category}</small><strong>{ui.categoryValue}</strong></span>
        <span><small>{ui.outputColumns}</small><strong>{isAdobe ? ui.adobeColumns : ui.shutterstockColumns}</strong></span>
      </div>
    </div>
  );
};

const ExportPreview = ({ ui, platform, compact = false }) => (
  <div className={`mss-export-preview ${compact ? 'mss-export-preview--compact' : ''}`}>
    <span className="mss-preview-label">{ui.exportFormats}</span>
    <div className="mss-export-formats">
      <span className="is-selected">CSV<i aria-hidden="true">✓</i></span>
      <span>XMP<i aria-hidden="true">✓</i></span>
      <span>{ui.embedded}<i aria-hidden="true">✓</i></span>
      <span>ZIP<i aria-hidden="true">✓</i></span>
    </div>
    <div className="mss-export-package">
      <div className="mss-package-icon" aria-hidden="true"><i /><i /><i /></div>
      <span>
        <small>{platform === 'adobe' ? 'Adobe Stock CSV' : 'Shutterstock CSV'}</small>
        <strong>{ui.exportStatus}</strong>
      </span>
      <em><span aria-hidden="true">✓</span>{ui.ready}</em>
    </div>
  </div>
);

const SceneFocus = ({ index, ui, platform, onPlatformChange, batchEdited, onBatchEditChange, compact = false, flutedReveal = '' }) => {
  if (index === 0) {
    return (
      <div className="mss-upload-focus">
        <div className="mss-focus-head"><strong>{ui.queue}</strong><span>{ui.queueStatus}</span></div>
        <QueueFiles compact={compact} />
        <div className="mss-processing-bar"><i /><span>{ui.complete}</span></div>
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="mss-analysis-focus">
        <div className="mss-focus-head"><strong>{ui.visualAnalysis}</strong><span className="mss-toggle is-on"><i />{ui.deepAnalysis}</span></div>
        <AssetPreview ui={ui} analysis flutedReveal={flutedReveal} />
      </div>
    );
  }

  if (index === 2) {
    return (
      <div className="mss-editor-focus">
        <AssetPreview ui={ui} />
        <FieldPreview label={ui.title} value={ui.generatedTitle} meta={`${ui.titleLength}: ${ui.medium}`} active />
      </div>
    );
  }

  if (index === 3) {
    return (
      <div className="mss-editor-focus">
        <AssetPreview ui={ui} />
        <div className="mss-description-stack">
          <FieldPreview label={ui.description} value={ui.generatedDescription} meta={`${ui.descriptionLength}: ${ui.medium}`} multiline active />
          <div className="mss-instruction-row">
            <span><small>{ui.customInstructions}</small><strong>{ui.instruction}</strong></span>
            <i>{ui.prefix}</i><i>{ui.postfix}</i>
          </div>
        </div>
      </div>
    );
  }

  if (index === 4) return <KeywordPreview ui={ui} compact={compact} />;
  if (index === 5) return <BatchPreview ui={ui} edited={batchEdited} onEditChange={onBatchEditChange} compact={compact} />;
  if (index === 6) return <PlatformPreview ui={ui} platform={platform} onPlatformChange={onPlatformChange} compact={compact} />;
  return <ExportPreview ui={ui} platform={platform} compact={compact} />;
};

const StudioFrame = ({ activeIndex, scene, asset, theme, themeReady, ui, platform, onPlatformChange, batchEdited, onBatchEditChange, frameLabel, sceneLabel }) => (
  <div
    id="metadata-studio-story-scene"
    className="mss-studio-frame"
    data-active-scene={activeIndex}
    role="region"
    aria-label={`${frameLabel}: ${scene.title}`}
  >
    <div className="mss-studio-chrome">
      <div className="mss-window-controls" aria-hidden="true"><i className="dot-red" /><i className="dot-yellow" /><i className="dot-green" /></div>
      <strong><span className="mss-app-logo">❖</span>{ui.appName} <small className="mss-chrome-badge">v2.5</small></strong>
      <div className="mss-chrome-status"><i className="mss-status-dot" /><span>{ui.workspace}</span><span className="mss-live-count">{ui.active}</span></div>
    </div>
    <div className="mss-studio-canvas">
      {themeReady && asset ? (
        <picture
          key={`${theme}-${scene.id}`}
          className="mss-real-screen is-active"
          aria-hidden="true"
        >
          <img
            src={asset.src}
            srcSet={asset.srcSet}
            sizes="(max-width: 1320px) 60vw, 780px"
            width={asset.width}
            height={asset.height}
            loading="lazy"
            decoding="async"
            alt=""
          />
        </picture>
      ) : <div className="mss-real-screen-placeholder" aria-hidden="true" />}
      <div className="mss-screen-scrim" aria-hidden="true" />
      <div className="mss-scene-tag" aria-hidden="true">
        <span>{sceneLabel} {String(activeIndex + 1).padStart(2, '0')} / 08</span>
        <strong>{scene.kicker}</strong>
      </div>
      <div className="mss-scene-number" aria-hidden="true">{String(activeIndex + 1).padStart(2, '0')}</div>
      {DEFAULT_COPY.scenes.map((_, index) => (
        <div
          className={`mss-scene-focus mss-scene-focus--${index} ${activeIndex === index ? 'is-active' : ''}`}
          aria-hidden={activeIndex !== index}
          key={`story-visual-${index}`}
        >
          <SceneFocus
            index={index}
            ui={ui}
            platform={platform}
            onPlatformChange={onPlatformChange}
            batchEdited={batchEdited}
            onBatchEditChange={onBatchEditChange}
            flutedReveal={activeIndex === index && index === 1 ? 'desktop' : ''}
          />
        </div>
      ))}
    </div>
    <div className="mss-studio-caption">
      <span>{String(activeIndex + 1).padStart(2, '0')}</span>
      <p><small>{scene.kicker}</small><strong>{scene.title}</strong></p>
      <i aria-hidden="true"><b style={{ '--mss-progress': `${((activeIndex + 1) / DEFAULT_COPY.scenes.length) * 100}%` }} /></i>
    </div>
  </div>
);

const MobileScene = ({ index, asset, theme, themeReady, ui, platform, onPlatformChange, batchEdited, onBatchEditChange, label }) => {
  const sceneRef = useRef(null);
  const [shouldRenderScene, setShouldRenderScene] = useState(false);
  const [isSceneVisible, setIsSceneVisible] = useState(false);

  useEffect(() => {
    const node = sceneRef.current;
    if (!node) return undefined;
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setShouldRenderScene(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setShouldRenderScene(true);
      observer.disconnect();
    }, { rootMargin: '700px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const node = sceneRef.current;
    if (index !== 1 || !node) return undefined;
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setIsSceneVisible(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsSceneVisible(Boolean(entry?.isIntersecting));
    }, { threshold: 0.28 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={sceneRef}
      className={`mss-mobile-scene mss-mobile-scene--${index}`}
      data-visual-ready={shouldRenderScene ? 'true' : 'false'}
      role="group"
      aria-label={label}
    >
      {shouldRenderScene ? (
        <>
          {themeReady && asset ? (
            <picture className="mss-mobile-real-screen" key={`mobile-${theme}-${asset.id}`} aria-hidden="true">
              <img
                src={asset.mobileSrc}
                srcSet={asset.srcSet}
                sizes="(max-width: 560px) calc(100vw - 58px), 620px"
                width={asset.width}
                height={asset.height}
                loading="lazy"
                decoding="async"
                alt=""
              />
            </picture>
          ) : null}
          <SceneFocus
            index={index}
            ui={ui}
            platform={platform}
            onPlatformChange={onPlatformChange}
            batchEdited={batchEdited}
            onBatchEditChange={onBatchEditChange}
            compact
            flutedReveal={index === 1 && isSceneVisible ? 'mobile' : ''}
          />
        </>
      ) : <div className="mss-mobile-scene-placeholder" aria-hidden="true" />}
    </div>
  );
};

const MetadataStudioStory = ({
  language = 'en',
  theme = 'light',
  themeReady = false,
  content,
  copy,
  onOpenStudio,
  studioHref = '/metadata'
}) => {
  const story = useMemo(() => mergeStoryCopy(content, copy), [content, copy]);
  const scenes = story.scenes.slice(0, 8);
  const [activeIndex, setActiveIndex] = useState(0);
  const [platform, setPlatform] = useState('adobe');
  const [batchEdited, setBatchEdited] = useState(true);
  const stepRefs = useRef([]);
  const studioAssets = useMemo(() => getMetadataStudioThemeAssets(theme), [theme]);

  useEffect(() => {
    if (typeof window === 'undefined' || !stepRefs.current.length) return undefined;

    let observer;
    let firstFrame;
    let secondFrame;
    let cancelled = false;

    const commitActiveIndex = (nextIndex) => {
      setActiveIndex((current) => (current === nextIndex ? current : nextIndex));
      const currentState = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
      if (currentState.stocktagMetadataStoryScene !== nextIndex) {
        window.history.replaceState(
          { ...currentState, stocktagMetadataStoryScene: nextIndex },
          document.title
        );
      }
    };

    const chooseNearestStep = () => {
      const anchor = window.innerHeight * 0.46;
      let nextIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      stepRefs.current.forEach((node, index) => {
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const focusPoint = Math.min(Math.max(anchor, rect.top), rect.bottom);
        const distance = Math.abs(focusPoint - anchor);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nextIndex = index;
        }
      });

      commitActiveIndex(nextIndex);
    };

    const startTracking = () => {
      if (cancelled) return;
      const savedIndex = Number(window.history.state?.stocktagMetadataStoryScene);
      const hasSavedIndex = Number.isInteger(savedIndex)
        && savedIndex >= 0
        && savedIndex < scenes.length;
      const navigationType = window.performance
        ?.getEntriesByType?.('navigation')?.[0]?.type;

      if (hasSavedIndex && navigationType === 'reload') {
        scrollSceneToViewportAnchor(stepRefs.current[savedIndex], 'auto');
        commitActiveIndex(savedIndex);
      } else {
        chooseNearestStep();
      }

      if (!('IntersectionObserver' in window)) return;
      observer = new IntersectionObserver(chooseNearestStep, {
        root: null,
        rootMargin: '-18% 0px -18% 0px',
        threshold: [0, 0.2, 0.5, 0.8, 1]
      });
      stepRefs.current.forEach((node) => {
        if (node) observer.observe(node);
      });
    };

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(startTracking);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      observer?.disconnect();
    };
  }, [scenes.length]);

  const moveToScene = (index) => {
    const nextIndex = Math.min(Math.max(index, 0), scenes.length - 1);
    setActiveIndex(nextIndex);
    if (typeof window !== 'undefined') {
      const currentState = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
      window.history.replaceState(
        { ...currentState, stocktagMetadataStoryScene: nextIndex },
        document.title
      );
    }
    const reducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    scrollSceneToViewportAnchor(
      stepRefs.current[nextIndex],
      reducedMotion ? 'auto' : 'smooth'
    );
  };

  const activeScene = scenes[activeIndex] || scenes[0] || DEFAULT_COPY.scenes[0];

  return (
    <section
      id="metadata-studio-story"
      className="metadata-studio-story"
      lang={language}
      data-active-scene={activeIndex}
      aria-labelledby="metadata-studio-story-title"
    >
      <div className="mss-story-shell">
        <header className="mss-story-intro">
          <p className="mss-story-eyebrow">
            <span className="mss-eyebrow-chip"><i className="mss-pulse-dot" />{story.ui.workflowLabel}</span>
            {story.eyebrow}
          </p>
          <h2 id="metadata-studio-story-title">
            {story.title}
          </h2>
          <p>{story.description}</p>
          <div className="mss-intro-badges">
            <span><i className="mss-badge-icon">✨</i> {story.ui.journeyBadge}</span>
            <span><i className="mss-badge-icon">⚡</i> {story.ui.controlsBadge}</span>
            <span><i className="mss-badge-icon">🎯</i> {story.ui.exportBadge}</span>
          </div>
        </header>

        <nav className="mss-progress" aria-label={story.progressLabel}>
          <span>{story.stepLabel}</span>
          <ol>
            {scenes.map((scene, index) => (
              <li key={scene.id || index}>
                <button
                  type="button"
                  onClick={() => moveToScene(index)}
                  aria-current={activeIndex === index ? 'step' : undefined}
                  aria-label={`${story.stepLabel} ${String(index + 1).padStart(2, '0')}: ${scene.title}`}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <small className="mss-progress-step-name">{scene.kicker}</small>
                  <i aria-hidden="true" />
                </button>
              </li>
            ))}
          </ol>
          <em>{String(activeIndex + 1).padStart(2, '0')} / {String(scenes.length).padStart(2, '0')}</em>
        </nav>

        <div className="mss-story-layout">
          <div className="mss-sticky-stage">
            <div className="mss-story-atmosphere" aria-hidden="true">
              <HomeGrainGradient className="mss-story-grain-shader" theme={theme} />
            </div>
            <StudioFrame
              activeIndex={activeIndex}
              scene={activeScene}
              asset={studioAssets.scenes[activeScene.id]}
              theme={theme}
              themeReady={themeReady}
              ui={story.ui}
              platform={platform}
              onPlatformChange={setPlatform}
              batchEdited={batchEdited}
              onBatchEditChange={setBatchEdited}
              frameLabel={story.studioFrameLabel}
              sceneLabel={story.sceneLabel}
            />
          </div>

          <div className="mss-story-steps">
            {scenes.map((scene, index) => (
              <article
                ref={(node) => { stepRefs.current[index] = node; }}
                className={`mss-story-step ${activeIndex === index ? 'is-active' : ''}`}
                id={`metadata-studio-story-step-${index + 1}`}
                data-story-scene={scene.id || index}
                data-workflow-step={index < 4 ? index + 1 : undefined}
                key={scene.id || index}
                aria-labelledby={`metadata-studio-story-step-title-${index + 1}`}
              >
                <div className="mss-step-copy">
                  <p>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    {scene.kicker}
                    <em className="mss-step-badge">{story.ui.phase} {index + 1}</em>
                  </p>
                  <h3 id={`metadata-studio-story-step-title-${index + 1}`}>{scene.title}</h3>
                  <p>{scene.description}</p>
                  <div className="mss-step-result">
                    <span aria-hidden="true">↳</span>
                    <p><small>{story.resultLabel}</small><strong>{scene.result}</strong></p>
                  </div>
                </div>
                <MobileScene
                  index={index}
                  asset={studioAssets.scenes[scene.id]}
                  theme={theme}
                  themeReady={themeReady}
                  ui={story.ui}
                  platform={platform}
                  onPlatformChange={setPlatform}
                  batchEdited={batchEdited}
                  onBatchEditChange={setBatchEdited}
                  label={`${story.sceneLabel} ${index + 1}: ${scene.title}`}
                />
              </article>
            ))}
          </div>
        </div>

        <footer className="mss-story-cta">
          <div className="mss-cta-summary">
            <span className="mss-cta-badge">⚡ {story.ui.verified}</span>
            <p><strong>{scenes[scenes.length - 1]?.result}</strong></p>
          </div>
          <a href={studioHref} onClick={(event) => handleActionClick(event, onOpenStudio)}>
            {story.openStudio}<span aria-hidden="true">→</span>
          </a>
        </footer>
      </div>
    </section>
  );
};

export default MetadataStudioStory;
